import { roundToDecimalPlaces } from '@semios/app-platform-common'
import type { TRowData } from 'App/irrigation-scheduler/utils/store/types'
import { translate } from 'i18n/i18n'
import { colors } from 'settings/colors'

const calculateBalance = (
  dailyEtc: (number | null)[],
  dailyRain: (number | null)[],
  dailyIrrigation: number[],
): (number | null)[] => {
  return dailyEtc.map((etc, idx) => {
    const rain = dailyRain[idx]

    return roundToDecimalPlaces((etc ?? 0) - (rain ?? 0) - (dailyIrrigation[idx] ?? 0), 1) ?? 0
  })
}

export const buildPlanningRowData = ({
  dailyEtc,
  dailyRain,
  dailyScheduled,
  dailyApplied,
  depthUnit,
  showUnitInLabel = true,
}: {
  dailyEtc: (number | null)[]
  dailyRain: (number | null)[]
  dailyScheduled?: number[]
  dailyApplied?: number[]
  depthUnit: string
  showUnitInLabel?: boolean
}): TRowData[] => {
  const totalEtc =
    roundToDecimalPlaces(
      dailyEtc.reduce<number>((sum, v) => sum + (v ?? 0), 0),
      1,
    ) ?? 0

  const totalRain =
    roundToDecimalPlaces(
      dailyRain.reduce<number>((sum, v) => sum + (v ?? 0), 0),
      1,
    ) ?? 0

  const labels = showUnitInLabel
    ? {
        etc: translate.phrases.banyanApp('ETc ({{unit}})', { unit: depthUnit }),
        rain: translate.phrases.banyanApp('Rain ({{unit}})', { unit: depthUnit }),
        planned: translate.phrases.banyanApp('Planned ({{unit}})', { unit: depthUnit }),
        applied: translate.phrases.banyanApp('Applied ({{unit}})', { unit: depthUnit }),
        plannedBalance: translate.phrases.banyanApp('Planned Balance ({{unit}})', { unit: depthUnit }),
        appliedBalance: translate.phrases.banyanApp('Applied Balance ({{unit}})', { unit: depthUnit }),
      }
    : {
        etc: translate.phrases.banyanApp('ETc'),
        rain: translate.phrases.banyanApp('Rain'),
        planned: translate.phrases.banyanApp('Planned'),
        applied: translate.phrases.banyanApp('Applied'),
        plannedBalance: translate.phrases.banyanApp('Planned Balance'),
        appliedBalance: translate.phrases.banyanApp('Applied Balance'),
      }

  const rows: TRowData[] = [
    {
      label: labels.etc,
      totalValue: totalEtc,
      dailyValues: dailyEtc,
    },
    {
      label: labels.rain,
      totalValue: totalRain,
      dailyValues: dailyRain,
    },
  ]

  if (dailyScheduled) {
    const totalScheduled =
      roundToDecimalPlaces(
        dailyScheduled.reduce((sum, v) => sum + v, 0),
        1,
      ) ?? 0

    rows.push({
      label: labels.planned,
      totalValue: totalScheduled,
      dailyValues: dailyScheduled,
    })
  }

  if (dailyApplied) {
    const totalApplied =
      roundToDecimalPlaces(
        dailyApplied.reduce((sum, v) => sum + v, 0),
        1,
      ) ?? 0

    rows.push({
      label: labels.applied,
      totalValue: totalApplied,
      dailyValues: dailyApplied,
    })
  }

  if (dailyScheduled) {
    const dailyBalanceScheduled = calculateBalance(dailyEtc, dailyRain, dailyScheduled)

    const balanceScheduled =
      roundToDecimalPlaces(
        dailyBalanceScheduled.reduce<number>((sum, v) => sum + (v ?? 0), 0),
        1,
      ) ?? 0

    rows.push({
      label: labels.plannedBalance,
      totalValue: balanceScheduled,
      dailyValues: dailyBalanceScheduled,
      backgroundColor: balanceScheduled > 0 ? colors.deficitWarning : undefined,
    })
  }

  if (dailyApplied) {
    const dailyBalanceApplied = calculateBalance(dailyEtc, dailyRain, dailyApplied)

    const balanceApplied =
      roundToDecimalPlaces(
        dailyBalanceApplied.reduce<number>((sum, v) => sum + (v ?? 0), 0),
        1,
      ) ?? 0

    rows.push({
      label: labels.appliedBalance,
      totalValue: balanceApplied,
      dailyValues: dailyBalanceApplied,
      backgroundColor: balanceApplied > 0 ? colors.deficitWarning : undefined,
    })
  }

  return rows
}
